import { z } from 'zod';
import { AppointmentSchema } from './api.js';
import { AppointmentOutcomeSchema } from './appointment-outcome.js';

// Booking loop (setter spec §05.2, §05.17, §06.4, §10.5, §15.7; owner decisions C7, C8): per-project reminders,
// advisor notification channels, outcome capture and the «resultado pendiente» list.

const Id = z.string().min(1).max(64);

/** Values the advisor notification template can receive (never budget or other sensitive data — §05.17 note). */
export const ADVISOR_NOTICE_FIELDS = ['advisor_name', 'contact_name', 'project_name', 'appointment_date', 'appointment_time', 'location', 'lead_link'] as const;
export const AdvisorNoticeFieldSchema = z.enum(ADVISOR_NOTICE_FIELDS);
export const ADVISOR_NOTICE_FIELD_LABELS: Record<z.infer<typeof AdvisorNoticeFieldSchema>, string> = {
  advisor_name: 'Nombre del asesor',
  contact_name: 'Nombre del prospecto',
  project_name: 'Proyecto',
  appointment_date: 'Fecha de la cita',
  appointment_time: 'Hora de la cita',
  location: 'Lugar',
  lead_link: 'Enlace seguro al prospecto en LIA',
};

export const REMINDER_OUTSIDE_WINDOW = ['shift_earlier', 'skip'] as const;
export const REMINDER_OUTSIDE_WINDOW_LABELS: Record<(typeof REMINDER_OUTSIDE_WINDOW)[number], string> = {
  shift_earlier: 'Adelantarlo al último horario permitido (p. ej. la noche anterior, antes de las 21:00)',
  skip: 'Omitirlo y dejar registro',
};

export const AppointmentLoopConfigSchema = z.object({
  /** §05.2 / §06.4 / §15.7: at booking, ~24 h before and 2–3 h before; always inside the contact window (C7). */
  reminders: z
    .object({
      enabled: z.boolean().default(true),
      confirmationAtBooking: z.boolean().default(true),
      dayBefore: z.boolean().default(true),
      dayBeforeHours: z.number().int().min(12).max(48).default(24),
      hoursBefore: z.boolean().default(true),
      /** Spec: 2–3 h. Default 2 h (PEND-02). */
      hoursBeforeHours: z.number().int().min(1).max(6).default(2),
      outsideWindow: z.enum(REMINDER_OUTSIDE_WINDOW).default('shift_earlier'),
      /** Two reminders closer than this collapse into the earlier one (same-day bookings). */
      minGapMinutes: z.number().int().min(15).max(720).default(60),
    })
    .prefault({}),
  /** §05.17 / §15.7: «por defecto CRM + WhatsApp». */
  advisorNotification: z
    .object({
      crm: z.boolean().default(true),
      whatsapp: z.boolean().default(true),
      /** Approved WhatsApp template for the notice; outside the advisor's 24 h window it is required. */
      whatsappTemplate: z
        .object({
          name: z.string().min(1).max(200),
          language: z.string().min(2).max(20),
          variables: z.record(z.string().max(40), AdvisorNoticeFieldSchema).default({}),
        })
        .nullable()
        .default(null),
    })
    .prefault({}),
  /** §10.5: the outcome is due the same day; after the end of that day + grace it shows «resultado pendiente». */
  outcome: z
    .object({
      graceHours: z.number().int().min(0).max(72).default(2),
      /**
       * UAT-32 / §10.5 «alertar/solicitar actualización al responsable humano»: while the outcome stays unreported the
       * request is repeated and escalated — 1st the advisor again, 2nd the project's incident owner (backup when there is
       * none), 3rd+ the workspace admins — every `intervalHours`, at most `maxEscalations` times, only inside the
       * project's contact hours. In LIA + WhatsApp (honest when there is no phone/template) + CRM task when mapped.
       * Never infers a no-show and never contacts the prospect.
       */
      escalation: z
        .object({
          enabled: z.boolean().default(true),
          intervalHours: z.number().int().min(4).max(168).default(24),
          maxEscalations: z.number().int().min(1).max(6).default(3),
          /** Approved template for the WhatsApp request outside the person's 24 h window (same fields as the advisor notice). */
          whatsappTemplate: z
            .object({
              name: z.string().min(1).max(200),
              language: z.string().min(2).max(20),
              variables: z.record(z.string().max(40), AdvisorNoticeFieldSchema).default({}),
            })
            .nullable()
            .default(null),
        })
        .prefault({}),
    })
    .prefault({}),
});
export type AppointmentLoopConfig = z.infer<typeof AppointmentLoopConfigSchema>;

export const AppointmentLoopConfigResponseSchema = z.object({
  projectId: Id,
  config: AppointmentLoopConfigSchema,
  /** false = never saved: spec defaults. */
  customized: z.boolean(),
  /** Contact window the reminders respect (project override or workspace), local time. */
  contactWindow: z.object({ start: z.string(), end: z.string(), timezone: z.string() }),
});
export type AppointmentLoopConfigResponse = z.infer<typeof AppointmentLoopConfigResponseSchema>;

export const RecordAppointmentOutcomeSchema = z.object({
  outcome: AppointmentOutcomeSchema,
  note: z.string().trim().max(1000).optional(),
});
export type RecordAppointmentOutcome = z.infer<typeof RecordAppointmentOutcomeSchema>;

export const PendingOutcomeQuerySchema = z.object({
  projectId: Id.optional(),
  /** mine = appointments I am responsible for; all = everything I can see (manager+). */
  scope: z.enum(['mine', 'all']).default('mine'),
});

export const PendingOutcomeItemSchema = AppointmentSchema.extend({
  leadInterestId: Id.nullable(),
  advisorMembershipId: Id.nullable(),
  /** Past the end-of-day + grace deadline (the human was alerted). */
  overdue: z.boolean(),
});
export type PendingOutcomeItem = z.infer<typeof PendingOutcomeItemSchema>;
